import { ApolloClient, InMemoryCache, gql } from "@apollo/client";
import { Kind } from "graphql";
import { backendURL } from "../constants";

export interface SortInput {
  [field: string]: "ASC" | "DESC";
}

const typeDefs = gql`
  scalar Date
`;

const resolvers = {
  Date: {
    __parseValue(value: string) {
      return new Date(value); // value from the client input variables
    },
    __serialize(value: Date) {
      return value.getTime(); // value sent to the client
    },
    __parseLiteral(ast: any) {
      if (ast.kind === Kind.INT) {
        return new Date(ast.value); // ast value is always in string format
      }
      return null;
    },
  },
};

export const client = new ApolloClient({
  uri: backendURL + "/graphql",
  cache: new InMemoryCache(),
  typeDefs,
  resolvers,
});

export const tokenOptions = (token: string | null) => {
  return {
    context: {
      headers: {
        authorization: `Bearer ${token}`,
      },
    },
  };
};

export const DUMMY_MUTATION = gql`
  mutation Dummy {
    dummy {
      success
    }
  }
`;

export const GET_USERS = gql`
  query Users(
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryUsersSortInput]
  ) {
    users(take: $take, skip: $skip, filter: $filter, sort: $sort) {
      items {
        id: iD
        firstName
        lastName
        lastLogin
        eMail
        role
        isDeveloper
      }
      totalItems
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser(
    $id: ID!
    $firstName: String
    $lastName: String
    $eMail: String
    $role: String
  ) {
    updateUser(
      iD: $id
      firstName: $firstName
      lastName: $lastName
      eMail: $eMail
      role: $role
    ) {
      id: iD
      firstName
      lastName
      lastLogin
      eMail
      role
      isDeveloper
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id) {
      success
    }
  }
`;

export const LAUNCH_TASK = gql`
  mutation LaunchTask($taskType: String!, $runs: Int!) {
    launchTask(taskType: $taskType, runs: $runs) {
      success
    }
  }
`;

export const RETRY_TASK = gql`
  mutation RetryTask($id: ID!) {
    retryTask(id: $id) {
      success
    }
  }
`;

export const GET_BACKEND_TASKS = gql`
  query BackendTasks(
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryBackendTasksSortInput]
  ) {
    backendTasks(take: $take, skip: $skip, filter: $filter, sort: $sort) {
      items {
        id: iD
        taskType
        createdAt
        isDone
        isError
        isInteractive
        attempts
        errorMessage
        errorTrace
        taskParametersJSon
        launchedBy {
          firstName
          lastName
        }
      }
      totalItems
    }
  }
`;

export const GET_LAST_DAILY_TASK = gql`
  query LastDailyTask {
    lastDailyTask {
      id: iD
      createdAt
      isError
      errorMessage
    }
  }
`;

export const GET_DIAMS = gql`
  query Diamonds(
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryDiamondsSortInput]
  ) {
    diamonds(take: $take, skip: $skip, filter: $filter, sort: $sort) {
      items {
        id: iD
        source
        sKU
        carat
        cut
        shape
        clarity
        color
        langermanColor
        flat
        melee
        isGIACertified
        mainImageID
        representationImageID
        price
        tags
        adminTags
        currentPricePercentile
        lastFantasyUpdate
        pricingRemark
        flat
        melee
        ignorePricing
        noLongerAvailable
        hasGoodImage
        hasWebImage
        hasOriginalImage
        hasPatternImage
        hasCalibratedImage
        hasImageProcessingError
        hasApproximateCalibration
      }
      totalItems
    }
  }
`;

// The colors a pricing report can be run for — the pricing sheets' own, not the stock's.
export const GET_PRICING_COLORS = gql`
  query PricingColors {
    getPricingColors
  }
`;

export const ANALYZE_PRICE = gql`
  mutation AnalyzePrice($color: String) {
    analyzePrice(color: $color) {
      buckets
      clarities
      prices {
        values
        equivalentColors {
          system
          color
          equivalentColors
        }
      }
      diams {
        id: iD
        source
        sKU
        color
        langermanColor
        flat
        melee
        clarity
        carat
        price
        perCaratNormalized
        isSellable
      }
      sources
      colorSpreads {
        system
        items {
          count
          equivalentColors {
            system
            color
            equivalentColors
          }
        }
      }
    }
  }
`;

export const ANALYZE_DIAMOND_PRICE = gql`
  mutation AnalyzeDiamondPrice($id: ID!) {
    analyzeDiamondPrice(id: $id) {
      buckets
      clarities
      schemes {
        pricing {
          values
          equivalentColors {
            system
            color
            equivalentColors
          }
        }
        diams {
          id: iD
          source
          sKU
          color
          langermanColor
          flat
          melee
          clarity
          carat
          price
          perCaratNormalized
          isSellable
          tags
        }
        pricingFactors
        suggestedPricingRange
        normalizedPricingRange
        myPercentile
        myPrice
        isValid
        colorSpreads {
          system
          items {
            count
            equivalentColors {
              system
              color
              equivalentColors
            }
          }
        }
      }
    }
  }
`;

export const GET_IMAGE_WITH_DIAMS = gql`
  query Images(
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryImagesSortInput]
  ) {
    images(take: $take, skip: $skip, filter: $filter, sort: $sort) {
      items {
        id: iD
        eXIF
        calibration
        diamond {
          id: iD
          source
          sKU
          representationImageID
          carat
          cut
          shape
          clarity
          color
          langermanColor
          flat
          melee
          price
          currentPricePercentile
          isGIACertified
          lastFantasyUpdate
          pricingRemark
          ignorePricing
          images {
            id: iD
            eXIF
            calibration
            origin
          }
          tags
          adminTags
          noLongerAvailable
        }
      }
      totalItems
    }
  }
`;

export const GET_DIAMS_WITH_IMAGE = gql`
  query Diamonds(
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryDiamondsSortInput]
  ) {
    diamonds(take: $take, skip: $skip, filter: $filter, sort: $sort) {
      items {
        id: iD
        source
        sKU
        representationImageID
        mainImageID
        carat
        cut
        shape
        clarity
        color
        langermanColor
        flat
        melee
        price
        currentPricePercentile
        isGIACertified
        tags
        adminTags
        noLongerAvailable
        lastFantasyUpdate
        pricingRemark
        ignorePricing
        images {
          id: iD
          eXIF
          calibration
          origin
        }
      }
      totalItems
    }
  }
`;

// The values the stock actually holds, for every selector that offers a stone characteristic.
export const GET_DIAMOND_OPTIONS = gql`
  query DiamondOptions {
    getDiamondOptions {
      gIAColors
      fancyIntensities
      fancySaturations
      fancyOvertones
      fancyColors
      langermanColors
      flatColors
      meleeColors
      shapes
      clarities
    }
  }
`;

// One color name in its three parts, for a search made from a stone.
export const SPLIT_FANCY_COLOR = gql`
  query SplitFancyColor($color: String!) {
    splitFancyColor(color: $color) {
      fancyIntensity
      fancySaturation
      fancyOvertone
      fancyColor
    }
  }
`;

export const GET_CLOSE_COLORS = gql`
  query ClosestColors(
    $okL: Float!
    $okC: Float!
    $okH: Float!
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryClosestColorsSortInput]
    $patternCalibratedOnly: Boolean
    $fancyIntensity: String
    $fancySaturation: String
    $fancyOvertone: String
    $fancyColor: String
    $langermanColor: String
    $flat: Int
    $melee: Int
    $excludeDiamondID: ID
    $webLikeOnly: Boolean
  ) {
    closestColors(
      okL: $okL
      okC: $okC
      okH: $okH
      take: $take
      skip: $skip
      filter: $filter
      sort: $sort
      patternCalibratedOnly: $patternCalibratedOnly
      fancyIntensity: $fancyIntensity
      fancySaturation: $fancySaturation
      fancyOvertone: $fancyOvertone
      fancyColor: $fancyColor
      langermanColor: $langermanColor
      flat: $flat
      melee: $melee
      excludeDiamondID: $excludeDiamondID
      webLikeOnly: $webLikeOnly
    ) {
      items {
        distance
        webLikeImageID
        originalImageID
        patternImageID
        diamondAnalysis {
          id: iD
          isComplete
          isError
          isIgnored
          colorPatchID
          oKL
          oKC
          oKH
          diamond {
            id: iD
            source
            sKU
            carat
            cut
            shape
            clarity
            color
            isGIACertified
            price
          }
        }
      }
      totalItems
    }
  }
`;

export const GET_IMAGE_ANALYSES = gql`
  query ImageAnalyses(
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryImageAnalysesSortInput]
  ) {
    imageAnalyses(take: $take, skip: $skip, filter: $filter, sort: $sort) {
      items {
        id: iD
        imageID
        isError
        isPredictedValid
        isForcedValid
        clipID
        detourID
        calibrationColors
        diamond {
          id: iD
          source
          sKU
          carat
          cut
          shape
          clarity
          color
          isGIACertified
          price
        }
        image {
          id: iD
          eXIF
          calibration
          boxMinX
          boxMinY
          boxMaxX
          boxMaxY
        }
      }
      totalItems
    }
  }
`;

// What a stone's color is: one per diamond, read from the histograms its calibration stored.
export const GET_DIAMOND_ANALYSES = gql`
  query DiamondAnalyses($take: Int, $skip: Int, $filter: String) {
    diamondAnalyses(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        isComplete
        isError
        isIgnored
        isApproximate
        colorPatchID
        histogramID
        oKL
        oKC
        oKH
      }
    }
  }
`;

// The stones with what regrading them could bring, as the worker stored it on their analysis.
export const GET_REGRADING_ALERTS = gql`
  query RegradingAlerts(
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryDiamondsSortInput]
  ) {
    diamonds(take: $take, skip: $skip, filter: $filter, sort: $sort) {
      items {
        id: iD
        source
        representationImageID
        sKU
        carat
        cut
        shape
        clarity
        color
        langermanColor
        flat
        melee
        isGIACertified
        tags
        adminTags
        price
        currentPricePercentile
        pricingRemark
        lastFantasyUpdate
        ignorePricing
        noLongerAvailable
        predictionRank
        regradingBetterProbability
        regradingLowestChange
        regradingHighestChange
      }
      totalItems
    }
  }
`;

// The colors each of a stone's pictures came closest to: a JSON of rankings by color system, then
// by picture name, each the first colors with their rank and score; and the stone's own color.
export const GET_DIAMOND_COLOR_RANKING = gql`
  query DiamondColorRanking($take: Int, $skip: Int, $filter: String) {
    diamondAnalyses(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        isComplete
        isIgnored
        colorRanking
        finalGrading
        oKL
        oKC
        oKH
      }
    }
  }
`;

// What a color's members look like, summed up by the worker: how many there are, how their one
// colors spread (a JSON of percentiles per axis) and their histograms drawn as one patch.
export const GET_COLOR_ANALYSES = gql`
  query ColorAnalyses($take: Int, $skip: Int, $filter: String) {
    colorAnalyses(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        colorID
        memberCount
        certifiedMemberCount
        percentiles
        grading
        colorPatchID
      }
    }
  }
`;

// A stone's price as its final grading has it, and, when GIA certified, what a regrading could
// bring. Computed by the worker on demand.
export const ANALYZE_GRADING_PRICE = gql`
  mutation AnalyzeGradingPrice($id: ID!) {
    analyzeGradingPrice(id: $id) {
      colors {
        colorID
        color
        probability
        median
      }
      covered
      curve {
        price
        probability
        below
      }
      p25
      p75
      regrading {
        current
        lowest
        highest
        betterProbability
      }
    }
  }
`;

// How well the final grading knows each color — its record and state — and its patch, by color.
export const GET_COLOR_GRADINGS = gql`
  query ColorGradings($take: Int, $skip: Int, $filter: String) {
    colorAnalyses(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        colorID
        grading
        percentiles
        colorPatchID
        color {
          id: iD
          system
          color
        }
      }
    }
  }
`;

// The GIA certified stones of a stone's lots — the same flat or melee number — each with its
// official color.
export const GET_LOT_MATES = gql`
  query LotMates($take: Int, $skip: Int, $filter: String) {
    diamonds(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        color
        flat
        melee
      }
    }
  }
`;

// The stones of a color, each with the one color its analysis read.
export const GET_COLOR_STONES = gql`
  query ColorStones($take: Int, $skip: Int, $filter: String) {
    diamonds(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        sKU
        isGIACertified
        mainImageID
        diamondAnalyses {
          oKL
          oKC
          oKH
        }
      }
    }
  }
`;

// A pattern has no ImageAnalysis, so its bounding box is read straight off the image.
// The image row itself — its box, and whether processing it failed. A picture with no analysis
// (a pattern, a web or a web-like one) carries its error here rather than on an ImageAnalysis.
export const GET_IMAGE_BOX = gql`
  query Images($take: Int, $skip: Int, $filter: String) {
    images(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        boxMinX
        boxMinY
        boxMaxX
        boxMaxY
        isError
      }
    }
  }
`;

// What a pattern picture tells us: its detour, and the colors read off it (a JSON object of RGB
// triples — the card's C, M, Y, K, W as measured, the stone's color once calibrated, and under
// "Targets" the five colors the card's were corrected to).
export const GET_PATTERN_ANALYSES = gql`
  query PatternAnalyses($take: Int, $skip: Int, $filter: String) {
    patternAnalyses(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        isError
        detourID
        colors
      }
    }
  }
`;

export const GET_TRAINING_BOXES = gql`
  query TrainingBoxes($take: Int!, $skip: Int!, $filter: String!) {
    trainingBoxes(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
      }
      totalItems
    }
  }
`;

export const GET_TRAINING_DETOURS = gql`
  query TrainingDetours($take: Int!, $skip: Int!, $filter: String!) {
    trainingDetours(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
      }
      totalItems
    }
  }
`;

export const GET_TRAINING_VALIDITIES = gql`
  query TrainingValidities($take: Int!, $skip: Int!, $filter: String!) {
    trainingValidities(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        isValid
      }
      totalItems
    }
  }
`;

export const SEND_TO_TRAINING = gql`
  mutation SendToTraining($imageID: ID!, $stage: String!) {
    sendToTraining(imageID: $imageID, stage: $stage) {
      id
    }
  }
`;

export const USED_IN_TRAINING = gql`
  query UsedInTraining($imageID: ID!) {
    usedInTraining(imageID: $imageID) {
      box
      detour
    }
  }
`;

export const FIX_PREDICTION = gql`
  mutation FixPrediction($id: ID!) {
    fixPrediction(id: $id) {
      isForcedValid
      isPredictedValid
    }
  }
`;

export const UPDATE_VALIDITY = gql`
  mutation UpdateValidity($id: ID!, $isValid: Boolean!) {
    updateValidity(id: $id, isValid: $isValid) {
      success
    }
  }
`;

export const REMOVE_VALIDITY = gql`
  mutation RemoveValidity($id: ID!) {
    removeValidity(id: $id) {
      success
    }
  }
`;

export const UPDATE_BOX = gql`
  mutation UpdateBox($id: ID!, $newMask: String!) {
    updateBox(id: $id, newMask: $newMask) {
      success
    }
  }
`;

export const REMOVE_BOX = gql`
  mutation RemoveBox($id: ID!) {
    removeBox(id: $id) {
      success
    }
  }
`;

export const UPDATE_DETOUR = gql`
  mutation UpdateDetour($id: ID!, $newGolden: String!) {
    updateDetour(id: $id, newGolden: $newGolden) {
      success
    }
  }
`;

export const REMOVE_DETOUR = gql`
  mutation RemoveDetour($id: ID!) {
    removeDetour(id: $id) {
      success
    }
  }
`;

// Every admin tag in use, for the editor to offer them back (admins only).
export const GET_ADMIN_TAGS = gql`
  query AdminTags {
    getAdminTags
  }
`;

// Every tag in use, sync tags and admin tags, with how many stones carry each.
export const GET_TAG_COUNTS = gql`
  query TagCounts {
    getTagCounts {
      tag
      kind
      count
    }
  }
`;

export const RENAME_ADMIN_TAG = gql`
  mutation RenameAdminTag($tag: String!, $newTag: String!) {
    renameAdminTag(tag: $tag, newTag: $newTag) {
      success
    }
  }
`;

export const DELETE_ADMIN_TAG = gql`
  mutation DeleteAdminTag($tag: String!) {
    deleteAdminTag(tag: $tag) {
      success
    }
  }
`;

// A stone's whole admin tag list, as the editor holds it (admins only).
export const UPDATE_ADMIN_TAGS = gql`
  mutation UpdateAdminTags($diamondID: ID!, $tags: [String!]!) {
    updateAdminTags(diamondID: $diamondID, tags: $tags) {
      success
    }
  }
`;

export const ANALYZE_DIAMOND = gql`
  mutation AnalyzeDiamond($diamondId: ID!) {
    analyzeDiamond(diamondId: $diamondId) {
      success
    }
  }
`;

export const RESET_BOX = gql`
  mutation ResetBox($imageId: ID!) {
    resetBox(imageId: $imageId) {
      success
    }
  }
`;

export const FORCE_RECALIBRATE = gql`
  mutation ForceRecalibrate($imageId: ID!) {
    forceRecalibrate(imageId: $imageId) {
      success
    }
  }
`;

export const SYNC_DIAMOND = gql`
  mutation SyncDiamond($id: ID!) {
    syncDiamond(id: $id) {
      success
    }
  }
`;

export const ADD_DASHBOARD_QUERY = gql`
  mutation AddDashboardQuery(
    $name: String!
    $query: String!
    $columns: String!
    $section: String!
    $adminOnly: Boolean!
  ) {
    addDashboardQuery(
      name: $name
      query: $query
      columns: $columns
      section: $section
      adminOnly: $adminOnly
    ) {
      success
    }
  }
`;

export const DELETE_QUERY = gql`
  mutation DeleteQuery($id: ID!) {
    deleteQuery(id: $id) {
      success
    }
  }
`;

export const UPDATE_DASHBOARD_QUERY_COLUMNS = gql`
  mutation UpdateDashboardQueryColumns($id: ID!, $columns: String!) {
    updateDashboardQueryColumns(id: $id, columns: $columns) {
      success
    }
  }
`;

export const UPDATE_DASHBOARD_QUERY_SECTION = gql`
  mutation UpdateDashboardQuerySection($id: ID!, $section: String!) {
    updateDashboardQuerySection(id: $id, section: $section) {
      success
    }
  }
`;

export const UPDATE_DASHBOARD_QUERY_NAME = gql`
  mutation UpdateDashboardQueryName($id: ID!, $name: String!) {
    updateDashboardQueryName(id: $id, name: $name) {
      success
    }
  }
`;

export const UPDATE_DASHBOARD_QUERY = gql`
  mutation UpdateDashboardQuery($id: ID!, $query: String!) {
    updateDashboardQuery(id: $id, query: $query) {
      success
    }
  }
`;

export const UPDATE_DASHBOARD_QUERY_VISIBILITY = gql`
  mutation UpdateDashboardQueryVisibility($id: ID!, $adminOnly: Boolean!) {
    updateDashboardQueryVisibility(id: $id, adminOnly: $adminOnly) {
      success
    }
  }
`;

export const GET_SAVED_QUERIES = gql`
  query SavedQueries($take: Int, $skip: Int, $filter: String!) {
    savedQueries(take: $take, skip: $skip, filter: $filter) {
      items {
        id: iD
        category
        name
        section
        query
        columns
        adminOnly
      }
      totalItems
    }
  }
`;

export const GET_COLOR_CLS = gql`
  query ColorCls(
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryColorClsSortInput]
  ) {
    colorCls(take: $take, skip: $skip, filter: $filter, sort: $sort) {
      items {
        id: iD
        system
        color
        valid
        exceptional
      }
      totalItems
    }
  }
`;

export const ADD_COLOR = gql`
  mutation AddColor(
    $system: String!
    $color: String!
    $valid: Boolean
    $exceptional: Boolean
  ) {
    addColor(
      system: $system
      color: $color
      valid: $valid
      exceptional: $exceptional
    ) {
      id: iD
      system
      color
      valid
      exceptional
    }
  }
`;

export const UPDATE_COLOR = gql`
  mutation UpdateColor($id: ID!, $valid: Boolean!, $exceptional: Boolean!) {
    updateColor(iD: $id, valid: $valid, exceptional: $exceptional) {
      id: iD
      system
      color
      valid
      exceptional
    }
  }
`;

export const DELETE_COLOR = gql`
  mutation DeleteColor($id: ID!) {
    deleteColor(id: $id) {
      success
    }
  }
`;

export const GET_COLOR_RELATIONSHIPS = gql`
  query ColorRelationships(
    $take: Int
    $skip: Int
    $filter: String
    $sort: [QueryColorRelationshipsSortInput]
  ) {
    colorRelationships(take: $take, skip: $skip, filter: $filter, sort: $sort) {
      items {
        id: iD
        type
        confirmedByData
        colorID
        color {
          id: iD
          system
          color
        }
        relatedColorID
        relatedColor {
          id: iD
          system
          color
        }
      }
      totalItems
    }
  }
`;

export const ADD_COLOR_RELATIONSHIPS = gql`
  mutation AddColorRelationships(
    $type: String!
    $colorID: ID!
    $relatedColorIDs: [ID!]!
  ) {
    addColorRelationships(
      type: $type
      colorID: $colorID
      relatedColorIDs: $relatedColorIDs
    ) {
      success
    }
  }
`;

export const DELETE_COLOR_RELATIONSHIPS = gql`
  mutation DeleteColorRelationships(
    $type: String!
    $colorID: ID!
    $relatedColorIDs: [ID!]!
  ) {
    deleteColorRelationships(
      type: $type
      colorID: $colorID
      relatedColorIDs: $relatedColorIDs
    ) {
      success
    }
  }
`;
